import {
  CreateTaskForm,
  EditTaskForm,
  TaskListsService,
  TaskPriority,
  TasksService,
} from '@api'
import { parse as parseDate } from 'date-fns'
import { z } from 'zod'

import {
  BaseEditTaskSchema,
  TaskBaseSchema,
} from '@/modules/tasks/service/task-base'

export const TaskSchema = TaskBaseSchema.extend({
  dueDate: z.string().nullish(),
  completed: z.boolean(),
  taskListId: z.number().nullish().optional(),
  taskList: z
    .object({
      id: z.number(),
      name: z.string(),
      taskIcon: z.string().optional(),
      color: z.string(),
      tasks: z.array(z.string()).optional(),
    })
    .nullish()
    .optional(),
})

export type Task = z.infer<typeof TaskSchema>

export const EditTaskSchema = BaseEditTaskSchema.extend({
  dueDate: z.string().nullish(),
  completed: z.boolean(),
})

export type TaskForm = z.infer<typeof EditTaskSchema>

export const ListTaskResponseSchema = z.array(TaskSchema)

function create(form: TaskForm) {
  const apiForm: CreateTaskForm = {
    name: form.name,
    detail: form.detail || undefined,
    priority: form.priority as TaskPriority | undefined,
    dueDate: form.dueDate || undefined,
    assignees: form.assignees || [],
  }

  return TasksService.createTask({ requestBody: apiForm }).then(
    TaskSchema.parse,
  )
}

function _delete(id: number) {
  return TasksService.deleteTask({ id })
}

export function list() {
  return TasksService.listTasks({}).then((response) =>
    ListTaskResponseSchema.parse(response),
  )
}

function update({ id, ...form }: Partial<TaskForm> & { id: number }) {
  // Convert our form to API format
  const apiForm: EditTaskForm = {
    name: form.name,
    detail: form.detail,
    priority: form.priority as TaskPriority | undefined,
    dueDate: form.dueDate,
    assignees: form.assignees || undefined,
    completed: form.completed,
  }

  return TasksService.editTask({ id, requestBody: apiForm }).then(
    TaskSchema.parse,
  )
}

function updateTaskList({ id, newListId }: { id: number; newListId: number }) {
  return TaskListsService.addTaskToTaskList({
    id: newListId,
    requestBody: { taskId: id },
  })
}

export const taskService = {
  create,
  delete: _delete,
  list,
  update,
  updateTaskList,
} as const

export type TaskService = typeof taskService

export function parseTaskDate(date: string | undefined | null) {
  if (date) {
    return parseDate(date, "yyyy-MM-dd'T'HH:mm:ss", new Date())
  }

  return null
}
